import {
  Controller,
  Control,
  UseFormSetValue,
  FieldErrors,
} from 'react-hook-form';
import { Grid } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import FieldRow from 'components/FormElements/FieldRow';
import AppNumberInput from 'components/FormElements/AppNumberInput';
import AppRadioButton from 'components/FormElements/AppRadioButton';
import AppTextInput from 'components/FormElements/AppTextInput';
import GridContainer from 'components/GridContainer';
import SharePriceInfoTooltip from './SharePriceInfoTooltip';
import TaxReliefElegibleAllocations from './TaxReliefElegibleAllocations';
import { TaxReliefElegibleType, ConsiderationType } from 'further-types/exit';
import { styled } from '@mui/material';
import type { ExitDetailsFormValues } from './index';

const StyledRow = styled('div')(() => ({
  display: 'flex',
  height: '42px',
  alignItems: 'center',
  gridGap: '1rem',

  '& > div': {
    width: '100%',
  },
}));

const StyledFieldRow = styled(FieldRow)(() => ({
  minHeight: '42px',
}));

const StyledErrorText = styled('p')(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: theme.fontsizes?.sm,
}));

type PreviousExitDetails = {
  firmId?: string;
  firmName?: string;
  exitDate?: Date | string;
  selectedCompany?:
    | string
    | { _id: string; legalName?: string; tradingName?: string };
  selectedCompanyId?: string;
  selectedShareClasses?: Array<string>;
  sharesToBeSold?: number;
  sharePrice?: number | string;
  taxableValueSharePrice?: number | string;
  chargeAccruedFees?: boolean;
  taxReliefElegible?: TaxReliefElegibleType;
  taxReliefElegibleAllocationIds?: Array<string>;
  shareAllocationUploadIds?: Array<string>;
  considerationType?: ConsiderationType;
  shareConsiderationCompanyId?: string;
  shareConsiderationShareClassIds?: Array<string> | string;
  totalConsiderationShares?: number | string;
  shareConsiderationValuePerShare?: number | string;
  shareConsiderationValueTotal?: number | string;
  shareConsiderationEisEligible?: boolean;
  shareConsiderationAllocationMethod?: string;
  permitPartialShareAllocation?: boolean;
};

type CashConsiderationFieldsProps = {
  control: Control<ExitDetailsFormValues>;
  setValue: UseFormSetValue<ExitDetailsFormValues>;
  errors: FieldErrors<ExitDetailsFormValues>;
  isEditMode: boolean;
  disableEdit: boolean;
  considerationType: ConsiderationType | undefined;
  totalConsiderationShares: string;
  taxReliefElegible: TaxReliefElegibleType | undefined;
  isReceivedValueSharePriceEdited: boolean;
  firmId: string | undefined;
  selectedShareClasses: Array<string>;
  selectedAllocations: Array<string>;
  taxReliefElegibleAllocationIds: Array<string>;
  previousExitDetails?: PreviousExitDetails;
};

const CashConsiderationFields: React.FC<CashConsiderationFieldsProps> = ({
  control,
  setValue,
  errors,
  isEditMode,
  disableEdit,
  considerationType,
  totalConsiderationShares,
  taxReliefElegible,
  isReceivedValueSharePriceEdited,
  firmId,
  selectedShareClasses,
  selectedAllocations,
  taxReliefElegibleAllocationIds,
  previousExitDetails,
}) => {
  if (considerationType !== ConsiderationType.Cash) {
    return null;
  }

  return (
    <>
      <StyledFieldRow
        title="Taxable value share price"
        centerTitle
        tooltipText="This is typically the share price agreed in any legal sale documentation."
      >
        <StyledRow>
          <Controller
            name="taxableValueSharePrice"
            control={control}
            render={({ field, fieldState: { error } }) =>
              isEditMode ? (
                <NumericFormat
                  onValueChange={({ value }) => field.onChange(value)}
                  value={field.value}
                  prefix="£"
                  allowNegative={false}
                  allowLeadingZeros={false}
                  thousandSeparator={true}
                  customInput={AppTextInput}
                  error={!!error}
                  helperText={error?.message}
                  disabled={disableEdit}
                />
              ) : (
                <AppNumberInput
                  value={Number(field.value)}
                  min={0}
                  decimals={12}
                  currencyStartAdornment
                  error={!!error}
                  helperText={error?.message}
                  disabled={disableEdit}
                  onChange={(value) => {
                    if (!isReceivedValueSharePriceEdited) {
                      setValue('sharePrice', value.toString());
                    }
                    field.onChange(value.toString());
                  }}
                />
              )
            }
          />
        </StyledRow>
      </StyledFieldRow>
      <StyledFieldRow
        title="Received value share price"
        centerTitle
        tooltipText={
          <span>
            This must be less than or equal to the{' '}
            <em>taxable value share price</em>, depending on whether all sale
            proceeds have been received or not.
          </span>
        }
      >
        <StyledRow>
          <Controller
            name="sharePrice"
            control={control}
            render={({ field, fieldState: { error } }) =>
              isEditMode ? (
                <NumericFormat
                  onValueChange={({ value }) => field.onChange(value)}
                  value={field.value}
                  prefix="£"
                  allowNegative={false}
                  allowLeadingZeros={false}
                  thousandSeparator={true}
                  customInput={AppTextInput}
                  error={!!error}
                  helperText={error?.message}
                  disabled={disableEdit}
                />
              ) : (
                <AppNumberInput
                  value={Number(field.value)}
                  min={0}
                  decimals={12}
                  currencyStartAdornment
                  error={!!error}
                  helperText={error?.message}
                  disabled={disableEdit}
                  onChange={(value) => {
                    setValue('isReceivedValueSharePriceEdited', true);
                    field.onChange(value.toString());
                  }}
                />
              )
            }
          />
        </StyledRow>
      </StyledFieldRow>
      <StyledFieldRow
        title="Charge accrued fees"
        centerTitle
        tooltipText="Deduct any fees that have been accrued to date but not paid by the investor. The maximum fee chargeable to any investor on exit is their full exit amount. Any accrued fees not charged now will remain on the system for charging at a later date."
      >
        <Controller
          name="chargeAccruedFees"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <AppRadioButton
                name="chargeAccruedFees"
                label="Yes"
                value={1}
                checked={field.value === true}
                onChange={(event) => {
                  field.onChange(
                    (event.target as HTMLInputElement).value === '1',
                  );
                }}
                error={!!error}
                disabled={disableEdit}
              />
              <AppRadioButton
                name="chargeAccruedFees"
                label="No"
                value={0}
                checked={field.value === false}
                onChange={(event) => {
                  field.onChange(
                    (event.target as HTMLInputElement).value === '1',
                  );
                }}
                error={!!error}
                disabled={disableEdit}
              />
              {error && <StyledErrorText>{error.message}</StyledErrorText>}
            </>
          )}
        />
      </StyledFieldRow>
      {(considerationType === ConsiderationType.Cash ||
        (considerationType === ConsiderationType.Share &&
          totalConsiderationShares &&
          Number(totalConsiderationShares) > 0)) && (
        <StyledFieldRow
          title="S/EIS eligible exit"
          centerTitle
          tooltipText={
            <span>
              {isEditMode && (
                <>
                  <span>
                    Editing the S/EIS status of this exit will change the status
                    from the original exit's date, not the date of this edit.
                  </span>
                  <br />
                  <br />
                </>
              )}
              <span>
                If this exit takes place over three years since your last EIS
                subscription in the company, select <em>Yes</em>. If this exit
                takes place within three years of your first EIS subscription in
                the company, select <em>No</em>. Where some tranches invested
                over three years before this exit, but others invested more
                recently, select Partial and choose the allocations that are
                S/EIS eligible (i.e. select those that invested over three years
                before this exit).
              </span>
            </span>
          }
        >
          <Controller
            name="taxReliefElegible"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <AppRadioButton
                  name="taxReliefElegible"
                  label="Yes"
                  value={1}
                  checked={field.value === TaxReliefElegibleType.Yes}
                  onChange={(_, checked) => {
                    if (checked) {
                      field.onChange(TaxReliefElegibleType.Yes);
                    }
                  }}
                  error={!!error}
                  disabled={disableEdit}
                />
                <AppRadioButton
                  name="taxReliefElegible"
                  label="No"
                  value={0}
                  checked={field.value === TaxReliefElegibleType.No}
                  onChange={(_, checked) => {
                    if (checked) {
                      field.onChange(TaxReliefElegibleType.No);
                    }
                  }}
                  error={!!error}
                  disabled={disableEdit}
                />
                <AppRadioButton
                  name="taxReliefElegible"
                  label="Partial"
                  value={0}
                  checked={field.value === TaxReliefElegibleType.Partial}
                  onChange={(_, checked) => {
                    if (checked) {
                      field.onChange(TaxReliefElegibleType.Partial);
                    }
                  }}
                  error={!!error}
                  disabled={disableEdit}
                />
                {error && <StyledErrorText>{error.message}</StyledErrorText>}
              </>
            )}
          />
        </StyledFieldRow>
      )}
      {taxReliefElegible === TaxReliefElegibleType.Partial &&
        considerationType === ConsiderationType.Cash &&
        (isEditMode
          ? Array.isArray(previousExitDetails?.selectedShareClasses) &&
            previousExitDetails.selectedShareClasses.length > 0 &&
            Array.isArray(previousExitDetails?.shareAllocationUploadIds) &&
            previousExitDetails.shareAllocationUploadIds.length > 0
          : firmId &&
            selectedShareClasses.length > 0 &&
            selectedAllocations.length > 0) && (
          <TaxReliefElegibleAllocations
            firmId={firmId ?? ''}
            selectedShareClasses={
              isEditMode
                ? previousExitDetails?.selectedShareClasses ?? []
                : selectedShareClasses
            }
            selectedAllocations={
              isEditMode
                ? previousExitDetails?.shareAllocationUploadIds ?? []
                : selectedAllocations
            }
            taxReliefElegibleAllocationIds={taxReliefElegibleAllocationIds}
            onChange={(value) =>
              setValue('taxReliefElegibleAllocationIds', value)
            }
            disabled={disableEdit}
            error={errors.taxReliefElegibleAllocationIds?.message}
          />
        )}
      {considerationType === ConsiderationType.Cash && (
        <GridContainer>
          <Grid size={{ md: 10, xs: 12 }}>
            <SharePriceInfoTooltip />
          </Grid>
        </GridContainer>
      )}
    </>
  );
};

export default CashConsiderationFields;
